/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { differenceInDays } from 'date-fns';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSitePersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useSitePersonalizationQuery.ts';

import {
	ReverseTrialCohort,
	type useReverseTrialEligibleSignUpPropertiesQuery as useReverseTrialEligibleSignUpPropertiesQuery$data,
} from '../__types__/useReverseTrialEligibleSignUpPropertiesQuery.ts';

/**
 * Should check if the user is eligible for the reverse trial and is a site admin
 */
export const useReverseTrialEligible = () => {
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const { isSiteAdmin } = useContext(SPAViewContext);

	// SignUpProperties that are sent from BXP
	const { isReverseTrialer, loading, error } = useReverseTrialCohort({
		skip: !isSiteAdmin || !isPremium,
	});

	// Master FF to quickly enable / disable all Onboarding Experiences
	const isReverseTrialEligibleUser = isSiteAdmin && isPremium && fg('cc_onboarding_experience');

	const isReverseTrialTreatmentCohort = !loading && !error && isReverseTrialer;

	return {
		isReverseTrialEligible: isReverseTrialEligibleUser && isReverseTrialTreatmentCohort,
	};
};

export function useReverseTrialCohort({
	skip,
}: { skip?: boolean; fireExperimentExposure?: boolean } = {}) {
	const { cloudId } = useSessionData();

	const {
		data: reverseTrialData,
		loading: isReverseTrialLoading,
		error: reverseTrialError,
	} = useQuery<useReverseTrialEligibleSignUpPropertiesQuery$data>(
		graphql`
			query useReverseTrialEligibleSignUpPropertiesQuery {
				signUpProperties {
					reverseTrial
				}
			}
		`,
		{
			skip,
		},
	);

	const {
		loading: isSitePersonalizationLoading,
		error: sitePersonalizationError,
		attributes,
	} = useSitePersonalizationQuery({
		cloudId,
		skip,
	});

	const TRIAL_PERIOD_DAYS = 45;
	let isWithinFirstTrialPeriod = true; // Default to true because if the activation date is not available, this means that the site was recently provisioned

	const activationDateAttr = attributes?.find(
		(attr: { name: string; value: string }) => attr.name === 'confluence.ondemand.activationDate',
	);

	if (activationDateAttr && activationDateAttr.value) {
		const activationDate = new Date(activationDateAttr.value);
		const daysSinceActivation = differenceInDays(new Date(), activationDate);
		isWithinFirstTrialPeriod = daysSinceActivation <= TRIAL_PERIOD_DAYS;
	}

	const reverseTrialCohort = reverseTrialData?.signUpProperties?.reverseTrial;

	return {
		isReverseTrialer:
			isWithinFirstTrialPeriod && reverseTrialCohort === ReverseTrialCohort.ENROLLED,
		reverseTrialCohort,
		loading: skip ? false : isReverseTrialLoading || isSitePersonalizationLoading,
		error: skip ? null : reverseTrialError || sitePersonalizationError,
	};
}
